import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from 'vue-router';

import { useAuthStore } from '@/stores/auth';
import { onSessionExpired } from '@/composables/useApiClient';

/**
 * [[INFRA-04]] Auth layout: unauthenticated → /login. Auth guard added in
 * Session 3b — bootstrap on first protected nav, mark expired on mid-session
 * 401 from any `/api/*` call.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
  },
  {
    path: '/cache-manager',
    name: 'cache-manager',
    component: () => import('@/views/CacheManagerView.vue'),
  },
  {
    path: '/sitemaps',
    name: 'sitemaps',
    component: () => import('@/views/SitemapsListView.vue'),
  },
  {
    path: '/sitemaps/:id(\\d+)',
    name: 'sitemap-details',
    component: () => import('@/views/SitemapDetailsView.vue'),
    props: true,
  },
  {
    path: '/sitemap-sync/:id(\\d+)',
    name: 'sitemap-sync-run',
    component: () => import('@/views/SitemapSyncRunView.vue'),
    props: true,
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

/**
 * Global guard per briefing §7.4:
 *   - Public routes (login, not-found) — bypass.
 *   - status=unknown → bootstrap (probes /api/auth/me).
 *   - status!=authenticated → redirect to /login. If was 'expired' →
 *     append `?expired=1` flash channel for LoginView banner.
 *   - Preserve original path in `?redirect=...` for post-login restore.
 */
router.beforeEach(async (to) => {
  if (to.meta.public) {
    return true;
  }
  const auth = useAuthStore();
  if (auth.status === 'unknown') {
    await auth.bootstrap();
  }
  if (auth.status !== 'authenticated') {
    const wasExpired = auth.status === 'expired';
    return {
      name: 'login',
      query: {
        redirect: to.fullPath,
        ...(wasExpired ? { expired: '1' } : {}),
      },
    };
  }
  return true;
});

/**
 * Wire the global 401 hook → auth store + redirect. Registered once at
 * module load. The hook is invoked by useApiClient on any non-login 401.
 */
let hookRegistered = false;
export function registerSessionExpiredHook(): void {
  if (hookRegistered) return;
  hookRegistered = true;
  onSessionExpired(() => {
    const auth = useAuthStore();
    auth.markExpired();
    // Best-effort redirect — router may not yet be active; failure tolerated.
    void router.push({ name: 'login', query: { expired: '1' } }).catch(() => {
      /* swallow */
    });
  });
}

export default router;
